import { useCallback, useEffect, useState } from "react";

type UseHtsCfarExerciseParams = {
  enableClose: () => void;
  disableClose: () => void;
  onClose: () => void;
  onReady?: () => void;
  onClickCancel?: () => void;
  onClickConfirm?: () => void;
  onExerciseSuccess?: () => void;
};

type DimensionEventData = {
  type: "hts-dimension";
  data: {
    height: number;
    width: number;
  };
};

type ReadyEventData = {
  type: "hts-ready";
  data: {
    status: "success" | "error";
  };
};

type ExerciseEventData = {
  type: "hts-exercise";
  data: {
    status: "pending" | "success" | "error";
  };
};

type ExerciseClickCancelData = {
  type: "hts-exercise-click-cancel";
  data: null;
};

type ExerciseClickConfirmCancelData = {
  type: "hts-exercise-click-confirm";
  data: null;
};

type ExitEventData = {
  type: "hts-exit";
  data: null;
};

type ExerciseAppMessageData =
  | DimensionEventData
  | ReadyEventData
  | ExerciseEventData
  | ExitEventData
  | ExerciseClickCancelData
  | ExerciseClickConfirmCancelData;

export const useHtsCfarExerciseModal = ({
  enableClose,
  disableClose,
  onClose,
  onReady,
  onClickCancel,
  onClickConfirm,
  onExerciseSuccess,
}: UseHtsCfarExerciseParams) => {
  const [isExerciseAppReady, setIsExerciseAppReady] = useState(false);
  const [isExerciseAppSuccess, setIsExerciseAppSuccess] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(0);

  useEffect(() => {
    disableClose();
  }, [disableClose]);

  const handleExerciseSuccess = useCallback(() => {
    window.location.reload();
  }, []);

  const handleDimensionMessage = useCallback(
    (eventData: DimensionEventData) => {
      setIframeHeight(eventData.data.height);
    },
    []
  );

  const handleReadyMessage = useCallback(
    (event: ReadyEventData) => {
      if (event.data.status === 'success') {
        onReady?.();
      }
      enableClose();
      setIsExerciseAppReady(true);
    },
    [enableClose, onReady]
  );

  const handleExerciseMessage = useCallback(
    (eventData: ExerciseEventData) => {
      if (eventData.data.status === "error") {
        enableClose();
      } else {
        disableClose();
      }

      if (eventData.data.status === "success") {
        onExerciseSuccess?.();
        setIsExerciseAppSuccess(true);
      }
    },
    [enableClose, disableClose, onExerciseSuccess]
  );

  const handleExitMessage = useCallback(() => {
    if (isExerciseAppSuccess) {
      handleExerciseSuccess();
    }
    onClose();
  }, [isExerciseAppSuccess, handleExerciseSuccess, onClose]);

  const handleClickCancel = useCallback(() => {
    onClickCancel?.();
  }, [onClickCancel]);

  const handleClickConfirm = useCallback(() => {
    onClickConfirm?.();
  }, [onClickConfirm]);

  useEffect(() => {
    const parentWindow = window.top || window;

    const handleMessage = (event: MessageEvent<ExerciseAppMessageData>) => {
      switch (event.data.type) {
        case "hts-dimension":
          handleDimensionMessage(event.data);
          break;
        case "hts-ready":
          handleReadyMessage(event.data);
          break;
        case "hts-exercise":
          handleExerciseMessage(event.data);
          break;
        case "hts-exit":
          handleExitMessage();
          break;
        case "hts-exercise-click-cancel":
          handleClickCancel();
          break;
        case "hts-exercise-click-confirm":
          handleClickConfirm();
          break;
        default:
          break;
      }
    };

    parentWindow.addEventListener("message", handleMessage);

    return () => {
      parentWindow.removeEventListener("message", handleMessage);
    };
  }, [
    handleDimensionMessage,
    handleReadyMessage,
    handleExerciseMessage,
    handleExitMessage,
    handleClickCancel,
    handleClickConfirm,
  ]);

  return {
    isLoading: !isExerciseAppReady,
    iframeHeight,
  };
};
